<template>
  <div class="d-flex">
    <div
      class=""
      :class="{
        'issue-badge-padding': myStuffTreeMode && checkpoints.length === 0,
        'issue-badge-padding-container': isMagentaHover,
        'issue-badge-padding-container-without-hover': !isMagentaHover,
      }"
    >
      <EXTooltip
        v-if="
          !myStuffTreeMode &&
          ((useCountsVariant && !hasActiveCheckpointsInCounts) ||
            (!useCountsVariant &&
              checkpoints.length === 0 &&
              currentBadgeStatus === 'null'))
        "
        placement="bottom"
        :description="'You can only award a badge because there is no active checkpoint defined for this responsibility'"
        :delay="300"
      >
        <div class="issue-badge-container" @click="$emit('onClick')">
          <InspectionIssueBadgeLevelBox
            v-if="currentBadgeLevelKey == 'novice'"
            :badgeLevel="currentBadgeLevelKey"
          />
          <div
            class="d-flex issue-badge-container-item"
            v-for="item in localCheckpointsData"
            :key="item.proficiency_level"
          >
            <InspectionIssueBadgeCheckpointBox
              :type="item.label"
              :checkpointsValue="item.checkpoints"
              :checkpointLength="checkpoints.length"
              :currentBadgeStatus="currentBadgeStatus"
            />
            <InspectionIssueBadgeBar
              v-if="item.label == readyFor"
              :readyFor="readyFor"
            />
            <InspectionIssueBadgeLevelBox
              v-if="item.isCurrentLevel"
              :badgeLevel="item.label"
              :badgeStatus="currentBadgeStatus"
            />
          </div>
        </div>
      </EXTooltip>
      <EXTooltip
        v-else-if="
          myStuffTreeMode &&
          localCheckpointsData.length >= 0 &&
          ((useCountsVariant && !hasActiveCheckpointsInCounts) ||
            (!useCountsVariant &&
              (checkpoints.length === 0 || currentBadgeStatus !== 'null')))
        "
        placement="bottom"
        :description="'There is no active checklist to inspect this responsibility'"
        :delay="300"
      >
        <div class="issue-badge-container" @click="$emit('onClick')">
          <InspectionIssueBadgeLevelBox
            v-if="currentBadgeLevelKey == 'novice'"
            :badgeLevel="currentBadgeLevelKey"
          />
          <div
            class="d-flex issue-badge-container-item"
            v-for="item in localCheckpointsData"
            :key="item.proficiency_level"
          >
            <InspectionIssueBadgeCheckpointBox
              :type="item.label"
              :checkpointsValue="item.checkpoints"
              :checkpointLength="checkpoints.length"
              :currentBadgeStatus="currentBadgeStatus"
              :myStuffTreeMode="myStuffTreeMode"
            />
            <InspectionIssueBadgeBar
              v-if="item.label == readyFor"
              :readyFor="readyFor"
            />
            <InspectionIssueBadgeLevelBox
              v-if="item.isCurrentLevel"
              :badgeLevel="item.label"
              :badgeStatus="currentBadgeStatus"
              :myStuffTreeMode="myStuffTreeMode"
            />
          </div>
        </div>
      </EXTooltip>
      <div v-else>
        <div class="issue-badge-container" @click="$emit('onClick')">
          <InspectionIssueBadgeLevelBox
            v-if="currentBadgeLevelKey == 'novice'"
            :badgeLevel="currentBadgeLevelKey"
          />
          <div
            class="d-flex issue-badge-container-item"
            v-for="item in localCheckpointsData"
            :key="item.proficiency_level"
          >
            <InspectionIssueBadgeCheckpointBox
              :type="item.label"
              :checkpointsValue="item.checkpoints"
              :checkpointLength="checkpoints.length"
              :currentBadgeStatus="currentBadgeStatus"
            />
            <InspectionIssueBadgeBar
              v-if="item.label == readyFor"
              :readyFor="readyFor"
            />
            <InspectionIssueBadgeLevelBox
              v-if="item.isCurrentLevel"
              :badgeLevel="item.label"
              :badgeStatus="currentBadgeStatus"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="showInspectionButtons">
      <v-tooltip bottom v-if="shouldShowInspectButton(checkpoints)">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <Button
              label="Inspect"
              size="small"
              tertiary
              icon="InspectionButtonBadge.svg"
              backgroundColor="#7C3AED"
              @onClick="handleInspectClick()"
              class="inspect-btn"
            />
          </div>
        </template>
        <span>Click to open Inspection Form</span>
      </v-tooltip>

      <!-- Give Badge Button - Conditional visibility -->
      <v-tooltip bottom v-if="shouldShowGiveBadgeButton(checkpoints)">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <Button
              label="Give Badge"
              size="small"
              primary
              icon="InspectionButtonBadge.svg"
              backgroundColor="#000000"
              @onClick="handleGiveBadgeClick()"
              class="give-badge-btn"
            />
          </div>
        </template>
        <span>Responsibility has no Checklist. You can only give badges</span>
      </v-tooltip>
    </div>
  </div>
</template>
<script setup>
import InspectionIssueBadgeCheckpointBox from "@components/InspectionIssueBadge/InspectionIssueBadgeCheckpointBox.vue";
import InspectionIssueBadgeLevelBox from "@components/InspectionIssueBadge/InspectionIssueBadgeLevelBox.vue";
import InspectionIssueBadgeBar from "@components/InspectionIssueBadge/InspectionIssueBadgeBar.vue";
import Button from "@components/buttons/Button.vue";
import { computed, defineEmits } from "vue";
import {
  isBadgeLevelValue,
  normalizeBadgeLevel,
} from "@components/utils/normalizeBadgeLevel";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import {
  activeCheckpointsFromCounts,
  activeCheckpointsFromList,
} from "@components/EvaluationGrandBadge/badgeIssueRules";
const emit = defineEmits(["optionClick"]);
const props = defineProps({
  currentBadgeLevel: {
    type: String,
    required: false,
    validator: (value) => isBadgeLevelValue(value),
  },
  currentBadgeStatus: {
    type: String,
    required: false,
    validator: (value) =>
      ["forced", "good_standing", "obsolete", "null"].includes(value),
  },
  readyFor: {
    type: String,
    required: false,
    validator: (value) =>
      value === null ||
      (isBadgeLevelValue(value) && normalizeBadgeLevel(value) !== "novice"),
  },
  checkpoints: {
    type: Array,
    required: false,
    default: () => [],
  },
  myStuffTreeMode: {
    type: Boolean,
    default: false,
  },
  isMagentaHover: {
    type: Boolean,
    default: true,
  },
  showInspectionButtons: {
    type: Boolean,
    default: false,
  },
  useCountsVariant: {
    type: Boolean,
    default: false,
  },
  checkpointCounts: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});

const currentBadgeLevelKey = computed(
  () => normalizeBadgeLevel(props.currentBadgeLevel) || props.currentBadgeLevel
);

// Wire field prefixes of checkpointCounts (API contract names, not renamed).
const CHECKPOINT_COUNT_WIRE_PREFIX = {
  apprentice: "apprentice",
  proficient: "professional",
  coach: "coach",
  architect: "master",
};

const localCheckpointsData = computed(() => {
  // Use counts variant if enabled
  if (props.useCountsVariant) {
    return localCheckpointsDataFromCounts.value;
  }

  if (!props || !props?.checkpoints || !Array.isArray(props.checkpoints)) return [];

  const groupedByLevel = {};

  props.checkpoints.forEach((checkpoint) => {
    if (!checkpoint) return;
    const level = checkpoint.proficiency_level;

    if (!groupedByLevel[level]) {
      groupedByLevel[level] = [];
    }

    groupedByLevel[level].push(checkpoint);
  });

  const result = [];

  for (let level = 1; level <= 4; level++) {
    const checkpointsInLevel = groupedByLevel[level] || [];

    const notPassedCount = checkpointsInLevel.filter(
      (checkpoint) =>
        checkpoint.evaluation_status !== "passed" &&
        checkpoint.evaluation_status !== "not_applicable"
    ).length;

    result.push({
      proficiency_level: level,
      label: getLabelFromProficiencyLevel(level),
      count: checkpointsInLevel.length,
      checkpoints: checkpointsInLevel.length > 0 ? notPassedCount : null,
      isCurrentLevel:
        currentBadgeLevelKey.value == getLabelFromProficiencyLevel(level),
    });
  }

  return result;
});

const localCheckpointsDataFromCounts = computed(() => {
  if (!props.checkpointCounts || !props.useCountsVariant) return [];

  const levels = [
    { level: 1, label: "apprentice" },
    { level: 2, label: "proficient" },
    { level: 3, label: "coach" },
    { level: 4, label: "architect" },
  ];

  return levels.map(({ level, label }) => {
    const wirePrefix = CHECKPOINT_COUNT_WIRE_PREFIX[label];
    const totalCount =
      props.checkpointCounts[`${wirePrefix}_checkpoint_count`] || 0;
    const passedCount =
      props.checkpointCounts[`${wirePrefix}_passed_checkpoint_count`] || 0;
    const remainingCount = totalCount - passedCount;

    let checkpoints;
    if (totalCount === 0) {
      checkpoints = null; // Show X - no checkpoints
    } else if (remainingCount === 0) {
      checkpoints = 0; // Show tick - all passed
    } else {
      checkpoints = remainingCount; // Show remaining count
    }

    return {
      proficiency_level: level,
      label,
      count: totalCount,
      checkpoints,
      isCurrentLevel: currentBadgeLevelKey.value === label,
    };
  });
});

const readyFor = computed(() => {
  if (props.readyFor) return normalizeBadgeLevel(props.readyFor) || props.readyFor;
  if (!localCheckpointsData) return null;
  let readyForLevel = 0;
  let block = false;
  localCheckpointsData.value.forEach((checkpoint) => {
    if (!block && checkpoint.checkpoints == 0) {
      readyForLevel++;
    } else {
      block = true;
    }
  });
  if (readyForLevel > 0) {
    return getLabelFromProficiencyLevel(readyForLevel);
  }
  return null;
});

const hasCheckpointsWithCount = computed(() => {
  return localCheckpointsData.value.some((item) => item.count > 0);
});

const hasActiveCheckpointsInCounts = computed(() => {
  if (!props.useCountsVariant || !props.checkpointCounts) return false;

  return activeCheckpointsFromCounts(props.checkpointCounts) > 0;
});

const getLabelFromProficiencyLevel = (level) => {
  switch (level) {
    case 1:
      return "apprentice";
    case 2:
      return "proficient";
    case 3:
      return "coach";
    case 4:
      return "architect";
    default:
      return "unknown";
  }
};
const shouldShowInspectButton = (checkpoints) => {
  if (props.useCountsVariant) {
    return hasActiveCheckpointsInCounts.value;
  }

  return activeCheckpointsFromList(checkpoints) > 0;
};
const shouldShowGiveBadgeButton = (checkpoints) => {
  if (props.useCountsVariant) {
    return !hasActiveCheckpointsInCounts.value;
  }

  return activeCheckpointsFromList(checkpoints) === 0;
};
const handleInspectClick = () => {
  // Handle Inspect button click - emit optionClick to parent
  emit("optionClick");
};
const handleGiveBadgeClick = () => {
  // Handle Give Badge button click - emit optionClick to parent
  emit("optionClick");
};
</script>
<style scoped>
.issue-badge-padding-container {
  padding: 8px;
}
.issue-badge-padding-container-without-hover {
  padding: 8px;
}
.issue-badge-padding-container:hover {
  display: flex;
  justify-content: center;
  align-content: center;
  cursor: url("../assets/cursorArrow.svg"), auto;
  border-radius: 8px;
  background: #f2edff;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.07);
}
.issue-badge-padding:hover {
  cursor: auto;
  background: transparent;
  box-shadow: none;
}
.issue-badge-container {
  display: inline-flex;
  padding: 4px;
  background-color: #f9fafb;
  border-radius: 4px;
  border: 1px solid #eaecf0;
  gap: 4px;
}
.issue-badge-container-item {
  gap: 4px;
}
.inspect-btn {
  background-color: #7c3aed !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  line-height: 20px !important;
  letter-spacing: 0% !important;
  vertical-align: middle !important;
  margin: 0px !important;
  padding-inline: 14px !important;
  padding-block: 6px !important;
}

.give-badge-btn,
.give-upgrade-btn {
  background-color: #000000 !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  line-height: 20px !important;
  letter-spacing: 0% !important;
  vertical-align: middle !important;
  padding-inline: 14px !important;
  padding-block: 6px !important;
  width: fit-content !important;
}
</style>
